// services/appointmentService.js
import { API_URL } from '../config/api';
import { ERROR_MESSAGES } from '../constants/calendar';

/**
 * Service class for handling all appointment-related API calls
 */
export class AppointmentService {
  /**
   * Base fetch wrapper with error handling
   * @param {string} url - The API endpoint URL
   * @param {object} options - Fetch options
   * @returns {Promise<object>} Response data
   */
  static async baseFetch(url, options = {}) {
    try {
      let token = null;
      try {
        const session = localStorage.getItem('userSession');
        if (session) token = JSON.parse(session)?.user?.token;
        if (!token) {
          const user = localStorage.getItem('user');
          if (user) token = JSON.parse(user)?.token;
        }
      } catch {}

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...options.headers
        },
        ...options
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        err.status = response.status;
        throw err;
      }

      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  /**
   * Fetch appointments for a date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of appointments
   */
  static async fetchAppointments(startDate, endDate) {
    try {
      const url = `${API_URL}/api/appointments/all?startDate=${startDate}&endDate=${endDate}`;
      console.log(`Fetching appointments from: ${url}`);
      console.log(`Date range: ${startDate} to ${endDate}`);

      const data = await this.baseFetch(url);
      console.log(`Fetched ${data.appointments?.length || 0} appointments`);
      
      return data.appointments || [];
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw new Error(ERROR_MESSAGES.FETCH_APPOINTMENTS);
    }
  }
  /**
   * Fetch customer data by ID
   * @param {string|number} customerId - Customer ID
   * @returns {Promise<Object>} Customer data
   */
  static async fetchCustomerById(customerId) {
    try {
      if (!customerId || isNaN(parseInt(customerId))) {
        throw new Error('Invalid customer ID');
      }

      const url = `${API_URL}/api/customers/${customerId}`;
      console.log(`Fetching customer data from: ${url}`);

      const customer = await this.baseFetch(url);
      console.log('Fetched customer:', customer);
      
      return customer;
    } catch (error) {
      console.error('Error fetching customer by ID:', error);
      throw new Error(`Failed to fetch customer data: ${error.message}`);
    }
  }
  /**
   * Move an appointment to a new time/date/stylist
   * @param {number} appointmentId - The appointment ID
   * @param {object} moveData - Object containing EventStart, EventEnd, and optionally MeetingWith/MWuserID
   * @returns {Promise<object>} Updated appointment data
   */
  static async moveAppointment(appointmentId, moveData) {
    try {
      console.log('Moving appointment:', appointmentId, 'with data:', moveData);

      const data = await this.baseFetch(`${API_URL}/api/appointments/move/${appointmentId}`, {
        method: 'PATCH',
        body: JSON.stringify(moveData)
      });

      console.log('Appointment moved successfully:', data);
      return data;
    } catch (error) {
      console.error('Error moving appointment:', error);
      throw new Error(ERROR_MESSAGES.MOVE_APPOINTMENT);
    }
  }

  /**
 * Update customer's bDiniCare status
 * @param {string|number} customerId - Customer ID
 * @param {boolean|number} bDiniCare - Dini Care status (true/false or 1/0)
 * @returns {Promise<Object>} Updated customer data
 */
static async updateCustomerDiniCare(customerId, bDiniCare) {
  try {
    const url = `${API_URL}/api/customers/${customerId}/dini-care`;
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bDiniCare })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update customer Dini Care status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating customer Dini Care:', error);
    throw error;
  }
}
/**
 * Enhanced updateAppointment method that also updates customer bDiniCare if changed
 * @param {string|number} appointmentId - Appointment ID
 * @param {Object} appointmentData - Appointment data
 * @param {Object} originalData - Original appointment data for comparison
 * @returns {Promise<Object>} Updated appointment data
 */
static async updateAppointmentWithCustomerUpdate(appointmentId, appointmentData, originalData = null) {
  try {
    console.log('Updating appointment with customer data sync:', appointmentId);

    // First, check if customer's bDiniCare needs to be updated
    if (appointmentData.ClientID && 
        originalData && 
        appointmentData.bDiniCare !== originalData.bDiniCare) {
      
      console.log('Customer Dini Care status changed, updating customer record...');
      
      try {
        await this.updateCustomerDiniCare(appointmentData.ClientID, appointmentData.bDiniCare);
        console.log('Customer Dini Care updated successfully');
      } catch (customerError) {
        console.error('Failed to update customer Dini Care, but continuing with appointment update:', customerError);
        // Don't fail the appointment update if customer update fails
        // But you might want to show a warning to the user
      }
    }

    // Update the appointment using the existing method
    const result = await this.updateAppointment(appointmentId, appointmentData);
    
    return result;
  } catch (error) {
    console.error('Error updating appointment with customer sync:', error);
    throw error;
  }
}

  /**
   * Resize an appointment (change start or end time)
   * @param {number} appointmentId - The appointment ID
   * @param {object} resizeData - Object containing new EventStart and EventEnd
   * @returns {Promise<object>} Updated appointment data
   */
  static async resizeAppointment(appointmentId, resizeData) {
    try {
      console.log('Resizing appointment:', appointmentId, 'with data:', resizeData);

      const data = await this.baseFetch(`${API_URL}/api/appointments/move/${appointmentId}`, {
        method: 'PATCH',
        body: JSON.stringify(resizeData)
      });

      console.log('Appointment resized successfully:', data);
      return data;
    } catch (error) {
      console.error('Error resizing appointment:', error);
      throw new Error(ERROR_MESSAGES.RESIZE_APPOINTMENT);
    }
  }

  /**
   * Search appointments by query string
   * @param {string} query - Search query (client name, phone, etc.)
   * @returns {Promise<Array>} Array of matching appointments
   */
  static async searchAppointments(query) {
    try {
      if (!query.trim() || query.length < 2) {
        return [];
      }

      const url = `${API_URL}/api/appointments/search?q=${encodeURIComponent(query)}`;
      const data = await this.baseFetch(url);
      
      return data.appointments || [];
    } catch (error) {
      console.error('Error searching appointments:', error);
      throw new Error(ERROR_MESSAGES.SEARCH_APPOINTMENTS);
    }
  }

  /**
   * Fetch holidays for a date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of holidays
   */
  static async fetchHolidays(startDate, endDate) {
    try {
      const url = `${API_URL}/api/appointments/holidays?startDate=${startDate}&endDate=${endDate}`;
      const data = await this.baseFetch(url);
      
      return data.holidays || [];
    } catch (error) {
      console.error('Error fetching holidays:', error);
      throw new Error(ERROR_MESSAGES.FETCH_HOLIDAYS);
    }
  }

  /**
 * Create a new appointment
 * @param {object} appointmentData - Appointment data
 * @returns {Promise<object>} Created appointment data
 */
static async createAppointment(appointmentData) {
  try {
    console.log('Creating appointment with data:', appointmentData);

    const data = await this.baseFetch(`${API_URL}/api/appointments/create-appointment`, {
      method: 'POST',
      body: JSON.stringify(appointmentData)
    });

    console.log('Appointment created successfully:', data);
    return data;
  } catch (error) {
    console.error('Error creating appointment:', error);
    throw error; // Preserve the original error message from the API
  }
}

/**
 * Update an existing appointment
 * @param {number} appointmentId - The appointment ID
 * @param {object} appointmentData - Updated appointment data
 * @returns {Promise<object>} Updated appointment data
 */
static async updateAppointment(appointmentId, appointmentData) {
  try {
    console.log('Updating appointment:', appointmentId, 'with data:', appointmentData);

    const data = await this.baseFetch(`${API_URL}/api/appointments/update/${appointmentId}`, {
      method: 'PUT',
      body: JSON.stringify(appointmentData)
    });

    console.log('Appointment updated successfully:', data);
    return data;
  } catch (error) {
    console.error('Error updating appointment:', error);
    throw error;
  }
}



  /**
   * Get appointments for a specific stylist and date range
   * @param {number} stylistId - The stylist's user ID
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of stylist's appointments
   */
  static async fetchStylistAppointments(stylistId, startDate, endDate) {
    try {
      const url = `${API_URL}/api/appointments/stylist/${stylistId}?startDate=${startDate}&endDate=${endDate}`;
      const data = await this.baseFetch(url);
      
      return data.appointments || [];
    } catch (error) {
      console.error('Error fetching stylist appointments:', error);
      throw new Error('Failed to fetch stylist appointments');
    }
  }

  // /**
  //  * Get appointments for a specific office and date range
  //  * @param {string} office - Office identifier (pomona, lakewood, israel)
  //  * @param {string} startDate - Start date in YYYY-MM-DD format
  //  * @param {string} endDate - End date in YYYY-MM-DD format
  //  * @returns {Promise<Array>} Array of office appointments
  //  */
  // static async fetchOfficeAppointments(office, startDate, endDate) {
  //   try {
  //     const url = `${API_URL}/api/appointments/office/${office}?startDate=${startDate}&endDate=${endDate}`;
  //     const data = await this.baseFetch(url);
      
  //     return data.appointments || [];
  //   } catch (error) {
  //     console.error('Error fetching office appointments:', error);
  //     throw new Error('Failed to fetch office appointments');
  //   }
  // }


/**
 * Fetch customer appointments from API --filtered: showing only future appointments and non-cancelled
 * @param {number} customerNo - Customer number/ID
 * @returns {Array} Array of appointments or empty array if error
 */

  static async fetchDistributorUsers() {
    try {
      const data = await this.baseFetch(`${API_URL}/api/appointments/distributor-users`);
      return data.users || [];
    } catch (error) {
      console.error('Error fetching distributor users:', error);
      throw new Error('Failed to fetch distributor users');
    }
  }

  static async fetchCustomerOrders(customerId) {
    try {
      const data = await this.baseFetch(`${API_URL}/api/appointments/customer-orders/${customerId}`);
      return data.orders || [];
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      throw new Error('Failed to fetch customer orders');
    }
  }

  static async fetchFilteredAppointments(customerNo) {
    try {
      const response = await this.baseFetch(`${API_URL}/api/customers/${customerNo}/appointments/history`);
      return response.appointments || response || [];
    } catch (error) {
      console.error('Error fetching customer appointments:', error);
      throw new Error('Failed to fetch customer appointments');
    }
  }


}

